import React, { useState } from 'react';

export interface CriticalResultsBreakdownItem {
  label: string;
  count: number;
}

interface CriticalResultsBreakdownBarChartProps {
  title: string;
  items: CriticalResultsBreakdownItem[];
}

export default function CriticalResultsBreakdownBarChart({
  title,
  items,
}: Readonly<CriticalResultsBreakdownBarChartProps>) {
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const maxCount = Math.max(...items.map(item => item.count), 1);

  const yTicks = (() => {
    const step = Math.max(1, Math.ceil(maxCount / 4));
    const ticks: number[] = [];
    for (let value = 0; value <= maxCount; value += step) {
      ticks.unshift(value);
    }
    if (ticks[0] !== maxCount) {
      ticks.unshift(maxCount);
    }
    return [...new Set(ticks)].sort((a, b) => b - a);
  })();

  return (
    <section className="crd-panel crd-breakdown-chart">
      <header className="crd-panel-header">
        <h2 className="crd-panel-title">{title}</h2>
      </header>
      {items.length === 0 ? (
        <p className="crd-empty">No data for the selected filters.</p>
      ) : (
        <div className="crd-breakdown-body">
          <div
            className="crd-breakdown-y-axis"
            aria-hidden
          >
            {yTicks.map(tick => (
              <span
                key={`${title}-tick-${tick}`}
                className="crd-breakdown-y-tick"
              >
                {tick}
              </span>
            ))}
          </div>
          <div className="crd-breakdown-plot">
            <div
              className="crd-breakdown-grid"
              aria-hidden
            >
              {yTicks.map(tick => (
                <div
                  key={`${title}-grid-${tick}`}
                  className="crd-breakdown-grid-line"
                />
              ))}
            </div>
            <div className="crd-breakdown-bars">
              {items.map(item => {
                const heightPct = Math.max(item.count > 0 ? 8 : 0, (item.count / maxCount) * 100);
                const isHovered = hoveredLabel === item.label;
                return (
                  <button
                    type="button"
                    key={`${title}-${item.label}`}
                    className={[
                      'crd-breakdown-bar-col',
                      isHovered ? 'crd-breakdown-bar-col--hovered' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => setHoveredLabel(item.label)}
                    onMouseLeave={() => setHoveredLabel(null)}
                    onFocus={() => setHoveredLabel(item.label)}
                    onBlur={() => setHoveredLabel(null)}
                    aria-label={`${item.label}: ${item.count} alerts`}
                  >
                    {isHovered ? (
                      <div
                        className="crd-chart-tooltip crd-chart-tooltip--bar"
                        role="tooltip"
                      >
                        <p className="crd-chart-tooltip-title">{item.label}</p>
                        <p className="crd-chart-tooltip-row">
                          <span>Alerts</span>
                          <strong>{item.count}</strong>
                        </p>
                      </div>
                    ) : null}
                    <span className="crd-breakdown-bar-value">{item.count}</span>
                    <div className="crd-breakdown-bar-track">
                      <div
                        className="crd-breakdown-bar-fill"
                        style={{ height: `${heightPct}%` }}
                        aria-label={`${item.label}: ${item.count} alerts`}
                      />
                    </div>
                    <span className="crd-breakdown-bar-label">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
